import React from "react";
import classNames from "classnames";
import { Link, NavLink, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Id } from "@planzone/dto";
import { UseCases } from "@planzone/core";
import {
  useProjectStatusesRetrieval,
  useRetrieveCurrentPlanzone,
  useRetrieveProjects,
} from "@planzone/view-models";
import { useModal } from "@root/client/modal";
import { Safeguard } from "@scenes/safeguard";
import { ZoneAvatar } from "@common/zoneAvatar";
import { ScrollingText } from "@common/scrollingText";
import { Flag } from "@common/infiniteScroll";

import { Props } from "./types";
import { AddProjectButton } from "./addProjectButton";
import { ProjectsList } from "./projectsList";
import { Loader } from "./loader";
import styles from "./styles.scss";

function Zone(props: Props) {
  const { projectId } = useParams<{ projectId: Id }>();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { statuses } = useProjectStatusesRetrieval();
  const { pushView } = useModal();
  const filter = {
    projects: [],
    projectStatuses: statuses.filter((status) => status.label !== "pz_closed"),
    projectsTags: [],
    zones: [props.zone],
  };
  const {
    isRequestPending,
    index,
    canRetrieveNextProjects,
    projects,
    retrieveProjects,
  } = useRetrieveProjects(filter, "ascendingName", 10);
  const visibleProjects = projects.filter(
    (p) => p.status.label !== "pz_closed",
  );
  const hasSelectedProject = projects.find(({ uuid }) => uuid === projectId);
  const { t } = useTranslation();
  const getProjects = () =>
    !isRequestPending &&
    retrieveProjects({
      filter,
      sort: "ascendingName",
      size: 10,
      index,
    });

  return (
    <div
      className={classNames(styles.zone, {
        [styles.opened]: props.isSelected || hasSelectedProject,
        // [styles.selected]: props.isSelected || hasSelectedProject,
        [styles.sidebarClosed]: !props.isSideBarOpen,
      })}
    >
      <div className={styles.header}>
        <NavLink
          to={`/zones/${props.zone.uuid}`}
          className={({ isActive }) =>
            classNames(styles.name, { [styles.selected]: isActive })
          }
        >
          <div className={styles.avatar}>
            <ZoneAvatar {...props.zone} />
          </div>
          <ScrollingText>
            <span>{props.zone.name}</span>
          </ScrollingText>
        </NavLink>
        {props.isSideBarOpen && (
          <span className={styles.projectCount}>
            {props.zone.totalNumberOfProjectsInZone}
          </span>
        )}
        <Safeguard useCases={[UseCases.createProject]}>
          {({ canCreateProject }) =>
            props.isSideBarOpen &&
            props.isSelected &&
            canCreateProject && (
              <div
                className={styles.addProjectButton}
                onClick={() =>
                  planzone.uuid === "57ec7e80-dcbb-49af-b11f-665fa6e545c1"
                    ? pushView({
                        key: UseCases.useProjectModel,
                        data: { zone: props.zone },
                      })
                    : props.addProject(props.zone)
                }
              >
                <AddProjectButton className={styles.addProjectButtonIcon} />
              </div>
            )
          }
        </Safeguard>
      </div>
      {(props.isSelected || hasSelectedProject) && props.isSideBarOpen && (
        <div className={styles.projectsList}>
          {visibleProjects.length > 0 ? (
            <ProjectsList projects={visibleProjects} />
          ) : (
            !isRequestPending && (
              <span className={styles.noProjectFound}>
                {t("layout.notifications.noProjectsFound")}
              </span>
            )
          )}
          {isRequestPending && <Loader />}
          <Flag
            name={props.zone.uuid}
            isDisabled={!canRetrieveNextProjects || isRequestPending}
            retrieveItems={getProjects}
          />
        </div>
      )}
    </div>
  );
}

export { Zone };
